<template>
  <div
    ref="cardSlider"
    class="keen-slider"
    :style="{ overflow: 'visible' }">
    <slot />
  </div>
  <div
    v-if="slider && isMobile"
    class="flex h-10 items-center justify-center space-x-2">
    <div
      v-for="(_dot, i) in length || slider.slides.length"
      :key="i"
      class="h-2 w-2 rounded-full transition-colors"
      :class="{
        'bg-gray-600': theme === 'light' && i === current,
        'bg-gray-300 opacity-60': theme === 'light' && i !== current,
        'bg-white opacity-100 shadow-whiteCircle': theme === 'dark' && i === current,
        'bg-gradient-to-r from-gray-100 to-gray-100 opacity-30': theme === 'dark' && i !== current
      }" />
  </div>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue'

  import { type KeenSliderInstance, useKeenSlider } from 'keen-slider/vue.es'

  import { useScreenSize } from '../composables/use-screen-size'

  type SliderType = {
    theme?: 'dark' | 'light'
    length?: number
    mobilePerView?: number
    tabletPerView?: number
    desktopPerView?: number
    mobileSpacing?: number
    tabletSpacing?: number
    desktopSpacing?: number
  }

  const {
    length,
    desktopPerView,
    desktopSpacing,
    mobilePerView,
    mobileSpacing,
    tabletPerView,
    theme = 'dark'
  } = defineProps<SliderType>()
  const { isMobile } = useScreenSize()
  const current = ref(0)

  const update = (e: KeenSliderInstance) => {
    current.value = e.track.details.rel
  }

  // On length change we should update the slider instance
  // If the delay is removed it will not allow to slide when changing the length correctly
  watch(
    () => length,
    () => {
      setTimeout(() => {
        if (slider.value) {
          slider.value.update(undefined, 0)
        }
      }, 10)
    }
  )

  const [cardSlider, slider] = useKeenSlider({
    slides: {
      perView: desktopPerView ?? 3,
      spacing: desktopSpacing ?? 32
    },
    breakpoints: {
      '(max-width: 639px)': {
        slides: {
          perView: mobilePerView ?? 1,
          spacing: mobileSpacing ?? 16
        }
      },
      '(min-width: 640px) and (max-width: 767px)': {
        slides: {
          perView: tabletPerView ?? 1.5,
          spacing: mobileSpacing ?? 16
        }
      },
      '(min-width: 768px) and (max-width: 1023px)': {
        slides: {
          perView: 2.2,
          spacing: 16
        }
      }
    },
    slideChanged: e => update(e)
  })
</script>
